import { createRoot } from "react-dom/client";
import { useState, useEffect, useCallback, useRef } from "react";
import { ClerkProvider, useClerk, useUser } from "@clerk/clerk-react";
import {
  CheckoutProvider,
  PaymentElementProvider,
  PaymentElement,
  useCheckout,
  usePaymentElement,
} from "@clerk/clerk-react/experimental";
import LegalLinks from "@citeclerk/ui/LegalLinks"; // RUN PX AMENDMENT — Terms/Privacy on every purchase surface
import PurchaseResultModal from "@citeclerk/ui/PurchaseResultModal"; // RUN PX Task 4 — confirm, don't redirect

const PUBLISHABLE_KEY = window.__CLERK_PUBLISHABLE_KEY;

// Matters P4B / live cutover — the five-tier lineup. The UI keys on the Clerk plan SLUGS (stable Dev↔Prod);
// gating uses the slug everywhere (has({plan:slug})). Clerk's <CheckoutProvider planId> needs the cplan_ id, so
// ONE map resolves slug → cplan_ id at the checkout call only.
//
// ENV-DRIVEN so Dev↔Prod is a config flip, not a code edit: the committed DEFAULT below is the PRODUCTION ids
// (so a plain prod build is correct with nothing injected). To run against the DEV Clerk instance, inject the
// Dev ids as a global BEFORE the bundle loads — same pattern as window.__CLERK_PUBLISHABLE_KEY:
//   <script>window.__CC_PLAN_IDS = {
//     student:"cplan_3GNDj6MtPSrxuvKzeL3TbYZ6Bnb", student_ultra:"cplan_3GNDmqi0zra4zVO9FjsrZqqsBSz",
//     attorney:"cplan_3GNDuXv3mLgAuZAAzDn5e728Uhz", attorney_ultra:"cplan_3GNDyrndtUeQW6y0FvrpdxqzTsg" };</script>
// (Firm is not offered. free = no paid plan.)
const CC_PLAN_ID_BY_SLUG = (typeof window !== "undefined" && window.__CC_PLAN_IDS) || {
  student:        "cplan_3GNAwyBgGtSmCpUTZqZ3DkpR76H",
  student_ultra:  "cplan_3GNBWc7H5Kc0c9ezPZaxhAVKdg3",
  attorney:       "cplan_3GNBbpBjw3526hEbL9D2kBfmqdR",
  attorney_ultra: "cplan_3GNBgbvKhNuvremTUGr8JYvTzka",
};
// ═════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE CHECKOUT HEADER'S PRICE IS READ FROM @citeclerk/plan. 2026-08-15.
//
// It was a hand-typed table right here — and it was WRONG on Student: `{ month: 5, annual: 48 }`
// against a canonical $9/mo and $84/yr. This is the price a customer reads in the modal WITH THEIR
// CARD ON SCREEN, one click after the pricing page. So the two surfaces most likely to be compared
// by the same person in the same minute carried different numbers, and the cheaper one was here.
//
// Only the DISPLAY moved. The charge has always been Clerk's, keyed on CC_PLAN_ID_BY_SLUG above —
// which is why the wrong number was survivable at all: the customer was billed correctly and told
// something else. Reading the display from the same table the page reads makes them agree by
// construction rather than by review.
//
// TIER_LABEL comes from the table too: a label is not a price, but "Student Ultra" appearing here
// and nowhere else is the same class of hand-copy, and the table already has it.
// ═════════════════════════════════════════════════════════════════════════════════════════════
import { TIER_LABEL, PRICING } from "@citeclerk/plan";

const PLAN_LABELS = TIER_LABEL;

/** cents → "$16" / "$16.50". Whole dollars stay whole — a checkout must not read "$16.00". */
function usd(cents) {
  const n = Number(cents) || 0;
  return n % 100 === 0 ? "$" + n / 100 : "$" + (n / 100).toFixed(2);
}

function priceLine(planKey, period) {
  const p = PRICING[planKey];
  // `free` has no checkout; an unknown slug must render nothing rather than "$0", which would read
  // as "this plan is free" on a paid modal.
  if (!p || (!p.monthly && !p.annual)) return { amount: "", per: "", billedLine: "" };
  if (period === "annual") {
    // PRICING.annual IS the annual-billed-monthly rate, so the yearly total is × 12 — derived, never
    // a second literal. (The old code did the reverse, dividing a typed yearly figure by 12 and
    // ROUNDING, which is how $48/yr became "$4/mo" on a plan that costs $7.)
    return { amount: usd(p.annual), per: "/mo", billedLine: "Billed at " + usd(p.annual * 12) + " annually" };
  }
  return { amount: usd(p.monthly), per: "/mo", billedLine: "" };
}

const STRIPE_APPEARANCE = {
  colorPrimary:         "#003A8F",
  colorBackground:      "#ffffff",
  colorText:            "#1a1a1a",
  colorDanger:          "#b91c1c",
  colorTextSecondary:   "#6B86B5",
  colorTextPlaceholder: "#a8adb8",
  colorIcon:            "#6B86B5",
  fontFamily:           "'DM Sans', system-ui, sans-serif",
  fontSizeBase:         "0.9375rem",
  borderRadius:         "12px",
  spacingUnit:          "4px",
};

function waitForClerk(timeoutMs) {
  return new Promise((resolve) => {
    if (window.Clerk) return resolve(window.Clerk);
    let waited = 0;
    const step = 50;
    const t = setInterval(() => {
      if (window.Clerk) { clearInterval(t); resolve(window.Clerk); }
      else if ((waited += step) >= timeoutMs) { clearInterval(t); resolve(null); }
    }, step);
  });
}

function CiteClerkLogo() {
  return (
    <div className="cc-checkout-logo" aria-hidden="true">
      <svg viewBox="106 357 1238 372" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g fill="currentColor">
          <path d="M137.46 717.89 c-9.78 -2.32 -17.98 -10.88 -19.57 -20.18 -1.47 -9.78 0.61 -13.57 19.93 -34.98 24.70 -27.39 27.39 -29.96 32.78 -30.94 5.87 -1.10 10.15 1.10 22.01 11.86 9.05 8.19 9.17 8.44 9.17 14.19 0 4.28 -0.73 6.97 -2.69 9.42 -7.83 10.15 -43.29 48.43 -46.23 50.02 -3.79 1.96 -9.17 2.20 -15.41 0.61z"/>
          <path d="M194.45 635.33 c-6.36 -5.63 -11.74 -10.76 -11.86 -11.25 -0.24 -0.61 1.71 -3.18 4.40 -5.75 l4.65 -4.53 9.05 7.83 c5.01 4.40 10.64 9.29 12.60 11.01 l3.67 2.94 -4.04 5.14 c-2.20 2.69 -4.65 5.01 -5.38 4.89 -0.86 0 -6.73 -4.65 -13.09 -10.27z"/>
          <path d="M277.86 644.51 c-20.42 -2.94 -41.58 -10.76 -57.97 -21.52 -11.74 -7.70 -32.04 -28.01 -39.99 -39.99 -14.19 -21.16 -22.14 -47.70 -22.14 -73.38 0 -56.75 40.48 -110.80 95.88 -127.92 46.60 -14.43 98.20 -2.57 130.98 30.09 8.93 8.93 20.18 23.11 20.18 25.32 0 1.22 -12.47 10.40 -28.74 21.04 l-4.77 3.18 -3.06 -4.04 c-5.75 -7.95 -21.65 -22.50 -29.47 -27.03 -4.28 -2.57 -12.23 -6.11 -17.73 -7.95 -8.68 -3.06 -11.50 -3.42 -25.07 -3.42 -32.04 0 -49.16 6.97 -70.93 28.74 -8.44 8.32 -11.50 12.47 -15.53 20.79 -7.58 15.41 -9.42 23.73 -9.42 40.97 0 16.88 1.96 25.44 8.93 40.36 8.68 18.47 23.60 33.63 42.31 42.80 13.57 6.73 22.26 9.05 37.30 9.91 19.93 1.22 40.97 -5.01 57.97 -17.12 8.07 -5.75 19.93 -17.86 23.60 -23.97 1.10 -1.96 2.57 -3.67 2.94 -3.67 0.49 0 8.68 5.01 18.10 11.25 19.57 12.72 18.83 10.76 8.44 23.85 -6.11 7.70 -28.62 29.72 -30.33 29.72 -0.61 0 -4.28 2.20 -8.32 4.77 -4.04 2.69 -8.68 5.26 -10.52 5.75 -1.71 0.37 -3.55 1.22 -3.91 1.83 -0.37 0.61 -1.47 1.10 -2.32 1.10 -0.98 0 -4.65 1.35 -8.19 2.94 -10.76 4.77 -42.31 7.83 -58.21 5.63z"/>
          <path d="M644.51 618.82 c-21.28 -3.91 -39.01 -17.37 -47.94 -36.32 -10.64 -22.50 -8.44 -49.78 5.63 -70.44 13.57 -19.81 39.14 -30.57 62.98 -26.54 30.09 5.01 50.26 31.43 50.26 65.67 l0 10.15 -45.86 0 c-51.61 0 -49.04 -0.49 -45.37 9.17 2.69 6.85 9.66 14.06 17.37 17.86 4.89 2.32 7.83 2.94 15.78 2.94 8.44 -0.12 10.64 -0.61 16.27 -3.67 3.55 -1.96 8.19 -5.38 10.15 -7.70 2.08 -2.32 4.40 -3.79 5.26 -3.55 0.98 0.37 6.48 3.79 12.35 7.58 l10.52 6.73 -3.55 4.65 c-6.85 8.44 -13.09 13.21 -22.26 17.24 -5.01 2.20 -10.27 4.53 -11.62 5.38 -3.06 1.71 -22.50 2.32 -29.96 0.86z m39.62 -84.02 c-5.38 -23.24 -36.08 -31.19 -52.59 -13.57 -3.79 3.91 -9.05 13.09 -9.05 15.65 0 0.86 10.40 1.22 31.19 1.22 l31.19 0 -0.73 -3.30z"/>
          <path d="M805.94 618.21 c-34.73 -7.70 -61.27 -32.78 -71.18 -67.39 -2.20 -7.83 -2.81 -12.60 -2.81 -25.56 0 -18.59 2.57 -29.47 10.52 -44.64 11.99 -23.24 31.55 -39.87 56.38 -48.19 13.21 -4.53 38.89 -5.75 52.83 -2.69 14.80 3.30 30.21 11.74 40.48 22.01 4.65 4.77 9.66 10.76 11.01 13.33 l2.45 4.77 -2.45 1.71 c-6.24 4.53 -25.07 16.39 -26.05 16.39 -0.61 0 -1.83 -1.47 -2.81 -3.18 -4.53 -8.56 -20.79 -19.57 -33.02 -22.38 -8.19 -1.96 -21.04 -1.71 -23.85 0.49 -1.22 0.98 -3.55 1.83 -5.01 1.83 -7.22 0.12 -24.34 10.27 -30.82 18.34 -8.68 10.88 -14.80 27.88 -14.80 41.58 0 15.65 6.85 33.02 17.37 44.39 18.83 20.18 47.33 24.83 72.16 11.62 6.85 -3.67 11.13 -7.46 18.34 -16.88 3.30 -4.16 3.42 -4.28 6.73 -2.32 16.75 9.91 24.83 15.04 24.83 15.65 0 0.37 -2.81 4.40 -6.24 8.93 -9.54 12.72 -24.83 24.70 -35.34 27.76 -3.06 0.86 -7.09 2.45 -9.17 3.67 -5.14 2.69 -38.16 3.30 -49.53 0.73z"/>
          <path d="M1028.89 618.70 c-14.55 -2.45 -30.33 -11.13 -39.50 -21.65 -6.36 -7.34 -12.84 -20.79 -14.80 -30.57 -1.96 -10.27 -0.73 -28.13 2.45 -37.42 7.09 -20.30 22.38 -35.34 41.95 -41.21 6.97 -2.20 10.76 -2.45 22.75 -2.08 13.09 0.37 15.04 0.73 22.50 4.28 21.28 10.03 33.39 30.45 34.85 58.83 l0.73 12.47 -44.64 0 c-51.49 0 -50.51 -0.24 -46.47 8.81 3.30 7.58 10.27 14.92 17.12 18.34 8.68 4.16 21.77 3.91 31.06 -0.61 3.79 -1.83 8.68 -5.38 10.76 -7.70 2.08 -2.45 4.16 -4.16 4.65 -3.91 0.49 0.24 5.87 3.30 12.11 6.97 l11.25 6.73 -2.08 3.55 c-3.42 5.75 -15.16 15.90 -22.87 19.69 -13.57 6.60 -25.68 8.19 -41.83 5.50z m38.77 -83.04 c0 -6.48 -8.44 -17.49 -16.14 -21.16 -17 -8.07 -38.52 1.71 -43.78 19.69 l-1.10 3.91 30.57 0 c29.84 0 30.45 0 30.45 -2.45z"/>
          <path d="M539.70 616.50 c-11.01 -3.79 -19.08 -11.62 -23.11 -22.26 -1.71 -4.40 -2.20 -11.74 -2.69 -42.31 l-0.49 -37.06 -10.88 0 -10.88 0 0 -13.45 0 -13.45 11.01 0 11.01 0 0 -17.73 0 -17.73 16.51 0 16.39 0 0.37 17.49 0.37 17.37 15.65 0.37 15.65 0.37 -0.37 13.70 -0.37 13.70 -13.94 0.37 c-8.44 0.24 -14.68 0.86 -15.78 1.71 -1.35 1.22 -1.59 6.85 -1.22 31.67 0.37 28.37 0.49 30.45 2.94 33.75 1.35 1.83 4.77 4.16 7.46 5.14 4.28 1.59 5.87 1.59 12.47 -0.12 4.04 -0.98 7.46 -1.71 7.58 -1.71 0 0.12 0.73 5.63 1.71 12.35 1.83 13.82 1.59 14.31 -6.11 17.86 -6.73 3.06 -24.46 3.06 -33.26 0z"/>
          <path d="M441.49 552.17 l0 -64.21 17.12 0 17.12 0 -0.24 63.96 -0.37 63.84 -16.75 0.37 -16.88 0.37 0 -64.33z"/>
          <path d="M920.90 523.43 l0 -92.95 17.12 0 17.12 0 0 92.95 0 92.95 -17.12 0 -17.12 0 0 -92.95z"/>
          <path d="M1117.80 552.17 l0 -64.21 16.51 0 16.51 0 0.12 8.93 c0 8.68 0 8.81 2.08 5.75 2.94 -4.53 9.78 -10.76 13.94 -12.96 6.85 -3.55 16.75 -5.50 22.75 -4.40 l5.75 0.86 0.37 15.53 0.37 15.53 -8.44 -0.73 c-15.29 -1.22 -26.54 4.77 -32.90 17.73 l-3.42 6.97 -0.37 37.67 -0.49 37.55 -16.39 0 -16.39 0 0 -64.21z"/>
          <path d="M1214.05 615.52 c-0.49 -0.37 -0.86 -42.19 -0.86 -92.95 l0 -92.09 17.12 0 17.12 0 0.24 53.20 0.37 53.08 17.49 -20.42 c9.54 -11.25 19.08 -22.26 21.04 -24.46 l3.67 -3.91 19.45 0.24 19.32 0.37 -19.45 22.01 c-10.76 12.11 -20.91 23.85 -22.63 26.05 l-3.18 3.91 4.65 7.09 c2.57 3.79 13.94 20.55 25.19 37.18 11.37 16.63 20.67 30.45 20.67 30.82 0 0.37 -8.68 0.73 -19.20 0.73 l-19.32 0 -16.51 -25.19 c-9.05 -13.94 -17.12 -25.56 -18.10 -25.80 -0.98 -0.37 -4.28 1.83 -7.70 5.26 l-5.99 5.87 0 19.93 0 19.93 -16.27 0 c-9.05 0 -16.75 -0.37 -17.12 -0.86z"/>
          <path d="M449.08 466.32 c-10.15 -5.01 -14.31 -18.47 -8.68 -28.50 5.87 -10.27 20.18 -14.43 29.23 -8.19 6.36 4.28 8.93 8.56 9.54 16.14 0.73 8.32 -1.10 12.72 -7.22 18.34 -4.28 3.91 -5.50 4.28 -11.86 4.28 -3.91 0 -8.68 -0.98 -11.01 -2.08z"/>
        </g>
      </svg>
    </div>
  );
}

function SecureBadge() {
  return (
    <div className="cc-checkout-secure" aria-label="Secure checkout">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
      Secured by{" "}
      <a
        href="https://stripe.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: "2px" }}
      >Stripe</a>
    </div>
  );
}

function BtnSpinner() {
  return (
    <span style={{
      display: "inline-block", width: 14, height: 14,
      border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff",
      borderRadius: "50%", animation: "cc-checkout-spin 0.7s linear infinite",
      verticalAlign: "middle", marginRight: 8, flexShrink: 0,
    }} aria-hidden="true" />
  );
}

// Must live inside <CheckoutProvider> and <PaymentElementProvider>
// `planLabel`/`priceLine` are pre-formatted by CheckoutModal (it already computes them for the
// header); `onOutcome({kind, eyebrow, title, lines})` hands the result to the shared
// PurchaseResultModal — this component never shows success/failure itself any more.
function CheckoutContent({ planLabel, priceLine, onOutcome }) {
  const { checkout } = useCheckout();
  const { submit, isFormReady, isProviderReady } = usePaymentElement();
  const clerk = useClerk();
  const [error, setError]     = useState(null);
  // Post-click charge in flight. Drives the "Processing…" label/state — set ONLY
  // when the user clicks Subscribe, never by the mount-time checkout.start() setup.
  const [submitting, setSubmitting] = useState(false);
  // The real first error from start(), if it fails — surfaced in the UI + on window.
  const [startError, setStartError] = useState(null);

  // Start the subscription server-side ONCE on mount. The ref guard is LOAD-BEARING:
  // the previous condition-only guard (status === "needs_initialization" && !isStarting)
  // RE-ARMED on every failed start() — a 429 reverts status to "needs_initialization" and
  // isStarting to false, re-satisfying the condition → re-fire → another 429 → a retry
  // STORM (thousands of /checkouts calls; isStarting stuck true; the button never enables).
  // startedRef makes start() fire EXACTLY ONCE regardless of those reversions, dependency-
  // identity churn, or StrictMode double-invoke. No retry on failure (re-arming = new storm).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    if (checkout.status !== "needs_initialization") return;
    startedRef.current = true;
    checkout.start().catch((e) => {
      // Capture the TRUE first error so it's inspectable (do NOT retry — that restarts the storm).
      try {
        window.__ccCheckoutStartError = e;
        if (window.__CC_CHECKOUT_DEBUG) console.error("[checkout] start() failed:", e);
      } catch (_) {}
      setStartError(
        e?.errors?.[0]?.longMessage || e?.message ||
        "Couldn't start checkout. Please try again or email sales@citeclerk.com."
      );
    });
  }, [checkout.status]);

  const handleSubscribe = async () => {
    // Single-submit guard — a second click must never start a second charge.
    if (submitting || checkout.isConfirming) return;
    setError(null);
    setSubmitting(true);
    try {
      // Step 1: collect card → Stripe SetupIntent
      const { data, error: submitErr } = await submit();
      if (submitErr) {
        // Stripe already shows card errors in the field — don't duplicate.
        // Back to the ready "Subscribe" state so the user can correct + retry.
        setSubmitting(false);
        return;
      }
      // Step 2: confirm subscription with Clerk.
      // 🔴 RUN PX — `checkout.confirm()` DOES NOT THROW ON A DECLINE. Per @clerk/shared's own
      // type (__experimental_CheckoutInstance.confirm), it resolves `{data, error}` — a decline is
      // a normal result, not an exception. The result was previously discarded outright, so a
      // declined card fell straight through to "success" and the redirect. Checked here now.
      const confirmResult = await checkout.confirm(data);
      if (confirmResult?.error) {
        setSubmitting(false);
        onOutcome({
          kind: 'failure',
          eyebrow: planLabel ? `${planLabel} plan` : 'Subscription',
          title: "We couldn't complete that purchase",
          lines: [confirmResult.error?.errors?.[0]?.longMessage || confirmResult.error?.message
            || "Something went wrong. Please try again or email sales@citeclerk.com."],
        });
        return;
      }
      // Step 3: reload session so new plan claim is in the token immediately.
      // Without this, getUserPlan on the server returns "free" until the
      // cached JWT naturally expires (~60s after payment).
      try {
        await clerk.session?.reload();
      } catch (_) { /* non-fatal — plan will resolve on next token refresh */ }
      // Step 4: finalize (activates the subscription) — no `navigate`. Confirm, don't redirect:
      // the outcome modal reports success and the customer stays exactly where they were.
      await checkout.finalize();
      setSubmitting(false);
      onOutcome({
        kind: 'success',
        eyebrow: planLabel ? `${planLabel} plan` : 'Subscription',
        title: "You're subscribed",
        lines: priceLine ? [priceLine] : [],
      });
    } catch (e) {
      console.error("[checkout] error:", e);
      // Charge failed — return to the ready "Subscribe" state so the user can retry.
      setSubmitting(false);
      onOutcome({
        kind: 'failure',
        eyebrow: planLabel ? `${planLabel} plan` : 'Subscription',
        title: "We couldn't complete that purchase",
        lines: [e?.errors?.[0]?.longMessage || e?.message
          || "Something went wrong. Please try again or email sales@citeclerk.com."],
      });
    }
  };

  // "Processing…" reflects ONLY the post-click charge (submit + Clerk confirm) — NOT the
  // mount-time checkout.start() setup. isStarting gates the button disabled-state below,
  // but must never drive the label, or it reads "Processing…" before the user clicks.
  const isProcessing = submitting || checkout.isConfirming;
  // Submit-readiness gate (P108 — OBSERVED-cause fix, 3rd attempt).
  // P106 gated on isFormReady; P107 on (isFormReady || isProviderReady) — BOTH failed live
  // with a fully-filled card, so Clerk's element-ready flags are unreliable to gate on
  // (the one-time "ready" event and/or the client-secret context read false to this hook
  // even while the form is rendered and fillable). We STOP gating on them.
  // Instead: once checkout.start() has completed — the checkout is no longer `isStarting`
  // and is past `needs_initialization` — the PaymentElement is mounted and submit() can
  // run. Stripe's confirmSetup() INSIDE submit() validates the card and rejects an
  // incomplete one (handled below: submitErr → reset to "Subscribe"), so a clickable button
  // is safe. The single-submit guard + isProcessing still prevent double-charges and keep
  // "Processing…" to post-click only; isStarting still keeps it disabled during setup.
  const submitReady  = !checkout.isStarting && checkout.status !== "needs_initialization";
  const canSubscribe = submitReady && !isProcessing && checkout.status !== "completed";

  // Instrumentation (passive, no console spam): with the modal open + card filled, read
  // `window.__ccCheckoutGate` in the browser console to OBSERVE the live gate inputs and
  // see exactly which condition is false. Set window.__CC_CHECKOUT_DEBUG = true to also
  // stream the snapshot to the console on every render.
  useEffect(() => {
    const snap = {
      isFormReady, isProviderReady, isProcessing, submitting,
      isStarting: checkout.isStarting, isConfirming: checkout.isConfirming,
      status: checkout.status, submitReady, canSubscribe,
    };
    try {
      window.__ccCheckoutGate = snap;
      if (window.__CC_CHECKOUT_DEBUG) console.log("[checkout gate]", snap);
    } catch (_) {}
  });

  const clerkError = checkout.error
    ? checkout.error?.errors?.[0]?.longMessage || checkout.error?.message || "Checkout error — please try again."
    : null;
  const displayError = error || startError || clerkError;

  return (
    <>
      <PaymentElement
        fallback={
          <div className="cc-checkout-loading">
            <span className="cc-checkout-spinner" aria-hidden="true" />
            <span>Loading secure payment…</span>
          </div>
        }
      />

      {displayError && (
        <div className="cc-checkout-error" role="alert">{displayError}</div>
      )}

      <button
        type="button"
        className="cc-checkout-subscribe"
        disabled={!canSubscribe}
        style={isProcessing
          ? { background: "#003A8F", color: "#fff", cursor: "wait", opacity: 0.85 }
          : undefined}
        onClick={handleSubscribe}
      >
        {isProcessing ? <><BtnSpinner />Processing…</> : "Subscribe"}
      </button>

      <SecureBadge />
      {/* RUN PX AMENDMENT — a purchase surface, so Terms/Privacy must be reachable from it. */}
      <LegalLinks />
    </>
  );
}

function CheckoutModal() {
  const clerk  = useClerk();
  const { isLoaded, isSignedIn } = useUser();
  const [state, setState] = useState({ open: false, planKey: null, period: "month" });

  useEffect(() => {
    window.__openCheckout = (planKey, period) => {
      const per = period === "annual" ? "annual" : "month";
      if (clerk && !clerk.user) {
        // Remember which plan they wanted so we can resume checkout after sign-in.
        try {
          sessionStorage.setItem(
            "cc_pending_checkout",
            JSON.stringify({ planKey, period: per })
          );
        } catch (_) {}
        // Open our own Ultramarine modal (auth island patched window.Clerk.openSignIn).
        // Return to /pricing after sign-in; the resume effect reopens checkout there.
        if (window.Clerk && typeof window.Clerk.openSignIn === "function") {
          window.Clerk.openSignIn({ afterSignInUrl: "/pricing", redirectUrl: "/pricing" });
        } else if (clerk && typeof clerk.redirectToSignIn === "function") {
          // Fallback: hosted page, only if the custom modal isn't available.
          clerk.redirectToSignIn({ afterSignInUrl: "/pricing", afterSignUpUrl: "/pricing" });
        }
        return;
      }
      setState({ open: true, planKey, period: per });
    };
    return () => { try { delete window.__openCheckout; } catch (_) {} };
  }, [clerk]);

  // After returning from sign-in, resume checkout for the plan they clicked.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let pending = null;
    try {
      const raw = sessionStorage.getItem("cc_pending_checkout");
      if (raw) pending = JSON.parse(raw);
    } catch (_) {}
    if (pending && pending.planKey) {
      try { sessionStorage.removeItem("cc_pending_checkout"); } catch (_) {}
      const per = pending.period === "annual" ? "annual" : "month";
      setState({ open: true, planKey: pending.planKey, period: per });
    }
  }, [isLoaded, isSignedIn]);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    document.body.style.overflow = state.open ? "hidden" : "";
    function onKey(e) { if (e.key === "Escape" && state.open) close(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state.open, close]);

  const planId = CC_PLAN_ID_BY_SLUG[state.planKey]; // slug → cplan_ id, only for Clerk's CheckoutProvider
  const label  = PLAN_LABELS[state.planKey] || state.planKey || "";
  const { amount, per, billedLine } = priceLine(state.planKey, state.period);
  const ready  = state.open && isLoaded && isSignedIn && !!planId;

  // RUN PX Task 4 — the outcome (success or failure) is reported by the shared
  // PurchaseResultModal, never by a redirect. On success there is nothing left to do in the
  // card-entry dialog, so it closes; on failure it stays open (with the card still filled in)
  // so the customer can retry — the outcome modal layers on top with the real reason.
  const [outcome, setOutcome] = useState(null); // null | {kind, eyebrow, title, lines}
  const handleOutcome = useCallback((o) => {
    setOutcome(o);
    if (o.kind === "success") setState((s) => ({ ...s, open: false }));
  }, []);

  return (
    <>
      <div
        className={"cc-checkout-root" + (state.open ? " is-open" : "")}
        role="dialog" aria-modal="true" aria-labelledby="cc-checkout-plan-name"
      >
        <div className="cc-checkout-scrim" aria-hidden="true" onClick={close} />
        <div className="cc-checkout-panel">

          <div className="cc-checkout-header">
            <button type="button" className="cc-checkout-close"
              aria-label="Close checkout" onClick={close}>✕</button>
            <CiteClerkLogo />
            <h2 className="cc-checkout-plan-name" id="cc-checkout-plan-name">
              {label ? `${label} Plan` : ""}
            </h2>
            <p className="cc-checkout-plan-price">
              <span className="cc-checkout-price-amount">{amount}</span>
              <span className="cc-checkout-plan-period">{per}</span>
            </p>
            {billedLine && <p className="cc-checkout-billed-line">{billedLine}</p>}
          </div>

          <div className="cc-checkout-body">
            {ready ? (
              <CheckoutProvider for="user" planId={planId} planPeriod={state.period}>
                <PaymentElementProvider for="user" stripeAppearance={STRIPE_APPEARANCE}>
                  <CheckoutContent
                    planLabel={label}
                    priceLine={billedLine || `${amount}${per}`}
                    onOutcome={handleOutcome}
                  />
                </PaymentElementProvider>
              </CheckoutProvider>
            ) : (
              <>
                <div className="cc-checkout-loading">
                  <span className="cc-checkout-spinner" aria-hidden="true" />
                  <span>Setting up your checkout…</span>
                </div>
                <button type="button" className="cc-checkout-subscribe" disabled>
                  Subscribe
                </button>
                <SecureBadge />
              </>
            )}
          </div>

        </div>
      </div>

      <PurchaseResultModal
        open={!!outcome}
        onClose={() => setOutcome(null)}
        outcome={outcome?.kind}
        eyebrow={outcome?.eyebrow}
        title={outcome?.title}
        lines={outcome?.lines}
      />
    </>
  );
}

const mount = document.getElementById("cc-checkout-react-root");
if (mount && PUBLISHABLE_KEY && PUBLISHABLE_KEY.indexOf("PLACEHOLDER") === -1) {
  waitForClerk(8000).then((clerk) => {
    if (!clerk) return;
    createRoot(mount).render(
      <ClerkProvider Clerk={clerk} publishableKey={PUBLISHABLE_KEY}>
        <CheckoutModal />
      </ClerkProvider>
    );
  });
}
